import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  CreditCard, 
  CheckCircle, 
  XCircle, 
  Clock, 
  Calendar,
  Download,
  Eye
} from 'lucide-react';
import { SearchSelect } from '../../components/shared';
import DataTable from '../../components/shared/DataTable';
import DatePicker from '../../components/client/DatePicker';
import { useQuery } from '@tanstack/react-query';
import api from '../../services/authService';
import { formatDate, formatTime } from '../../utils/dateUtils';

const AdminPayments = () => {
  const navigate = useNavigate();
  const [filters, setFilters] = useState({
    status: '',
    payment_type: '',
    date_from: '',
    date_to: ''
  });

  // Status options for SearchSelect
  const statusOptions = [
    { value: '', label: 'All Statuses' },
    { value: 'completed', label: 'Completed' },
    { value: 'pending', label: 'Pending' },
    { value: 'failed', label: 'Failed' },
    { value: 'refunded', label: 'Refunded' }
  ];

  // Payment type options
  const paymentTypeOptions = [
    { value: '', label: 'All Types' },
    { value: 'deposit', label: 'Deposit' },
    { value: 'full_payment', label: 'Full Payment' }
  ];

  // Clean filters to remove empty values
  const cleanFilters = Object.fromEntries(
    Object.entries(filters).filter(([_, value]) => value !== '' && value !== null && value !== undefined)
  );

  // Build query string
  const queryString = new URLSearchParams(cleanFilters).toString();

  // Fetch all payments for admin view
  const { data: paymentsResponse, isLoading, error } = useQuery({
    queryKey: ['adminPayments', cleanFilters],
    queryFn: async () => {
      // Add per_page to get all payments (or a large number)
      const params = new URLSearchParams(cleanFilters);
      params.append('per_page', '1000'); // Get all payments
      const response = await api.get(`/admin/payments?${params.toString()}`);
      return response.data;
    }
  });

  // Extract payments from paginated response
  const allPayments = paymentsResponse?.data?.data || paymentsResponse?.data || [];
  // Filter out pending payments - don't show them
  const payments = allPayments.filter(payment => payment.status !== 'pending');

  // Get status configuration
  const getStatusConfig = (status) => {
    const statusConfig = {
      completed: { color: 'bg-green-100 text-green-800', icon: CheckCircle, label: 'Completed' },
      pending: { color: 'bg-yellow-100 text-yellow-800', icon: Clock, label: 'Pending' },
      failed: { color: 'bg-red-100 text-red-800', icon: XCircle, label: 'Failed' },
      refunded: { color: 'bg-gray-100 text-gray-800', icon: XCircle, label: 'Refunded' }
    };
    return statusConfig[status] || statusConfig.pending;
  };

  // Helper function to truncate text
  const truncateText = (text, maxLength = 12) => {
    if (!text) return 'N/A';
    if (text.length <= maxLength) return text;
    return text.substring(0, maxLength) + '...';
  };

  // Define table columns
  const columns = [
    {
      key: 'payment_booking',
      label: 'Payment / Booking',
      sortable: true,
      width: 'w-40',
      render: (payment) => {
        const paymentRef = payment.payment_reference || payment.id || '';
        const bookingRef = payment.booking?.booking_reference || payment.booking_id || 'N/A';
        
        return (
          <div className="min-w-0 space-y-1">
            <span className="font-mono text-xs font-medium text-primary-600 block truncate" title={`#${paymentRef}`}>
              #{truncateText(paymentRef, 12)}
            </span>
            {payment.booking_id ? (
              <button
                onClick={() => navigate(`/admin/bookings/${payment.booking_id}`)}
                className="font-mono text-xs font-medium text-primary-600 hover:underline block truncate"
                title={`#${bookingRef}`}
              >
                #{truncateText(bookingRef, 12)}
              </button>
            ) : (
              <span className="font-mono text-xs text-gray-400 block">N/A</span>
            )}
          </div>
        );
      }
    },
    {
      key: 'client',
      label: 'Client',
      sortable: true,
      width: 'w-48',
      render: (payment) => (
        <div className="min-w-0">
          <p className="text-sm font-medium text-gray-900 truncate">
            {payment.booking?.client?.name || 'Unknown Client'}
          </p>
          <p className="text-xs text-gray-500 truncate">
            {payment.booking?.client?.email || ''}
          </p>
        </div>
      )
    },
    {
      key: 'vendor',
      label: 'Vendor',
      sortable: true,
      width: 'w-48',
      render: (payment) => (
        <div className="min-w-0">
          <p className="text-sm font-medium text-gray-900 truncate">
            {payment.booking?.vendor?.business_name || 'Unknown Vendor'}
          </p>
        </div>
      )
    },
    {
      key: 'amount',
      label: 'Amount',
      sortable: true,
      width: 'w-24',
      render: (payment) => {
        const formatCurrency = (value) => {
          const num = parseFloat(value);
          if (isNaN(num) || num === null || num === undefined) {
            return '0.00';
          }
          return num.toFixed(2);
        };
        return (
          <div className="text-right">
            <p className="text-sm font-medium text-gray-900">
              ${formatCurrency(payment.amount)}
            </p>
            {payment.payment_type && (
              <p className="text-xs text-gray-500 mt-0.5 capitalize">
                {payment.payment_type.replace('_', ' ')}
              </p>
            )}
          </div>
        );
      }
    },
    {
      key: 'created_at',
      label: 'Date',
      sortable: true,
      width: 'w-32',
      render: (payment) => (
        <div className="flex items-center space-x-2">
          <Calendar className="w-4 h-4 text-gray-400 flex-shrink-0" />
          <div className="min-w-0">
            <div className="text-sm font-medium text-gray-900">
              {formatDate(payment.created_at, 'medium')}
            </div>
            {payment.created_at && (
              <div className="text-xs text-gray-500">
                {formatTime(payment.created_at)}
              </div>
            )}
          </div>
        </div>
      )
    },
    {
      key: 'status',
      label: 'Status',
      sortable: true,
      width: 'w-28',
      render: (payment) => {
        const statusConfig = getStatusConfig(payment.status);
        const StatusIcon = statusConfig.icon;
        return (
          <span className="whitespace-nowrap flex-shrink-0">
            <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${statusConfig.color}`}>
              <StatusIcon className="w-3 h-3 mr-1" />
              {statusConfig.label}
            </span>
          </span>
        );
      }
    }
  ];

  // Define table actions
  const actions = [
    {
      label: 'View Booking',
      icon: Eye,
      variant: 'default',
      onClick: (payment) => {
        if (payment.booking_id) {
          navigate(`/admin/bookings/${payment.booking_id}`);
        }
      }
    }
  ];

  // Handle search
  const handleSearch = (data, searchTerm) => {
    if (!searchTerm) return data;
    
    return data.filter(payment => 
      payment.payment_reference?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      payment.booking?.booking_reference?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      payment.booking?.client?.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      payment.booking?.client?.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      payment.booking?.vendor?.business_name?.toLowerCase().includes(searchTerm.toLowerCase())
    );
  };

  // Handle filter changes
  const handleFilterChange = (key, value) => {
    setFilters(prev => ({
      ...prev,
      [key]: value
    }));
  };

  // Calculate totals (excluding pending)
  const totalAmount = payments
    .filter(p => p.status === 'completed')
    .reduce((sum, p) => sum + parseFloat(p.amount || 0), 0);

  const totalPayments = payments.length;
  const completedPayments = payments.filter(p => p.status === 'completed').length;

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 p-3 sm:p-6">
        <div className="bg-white border border-gray-200 rounded-lg p-6">
          <div className="text-center">
            <XCircle className="w-12 h-12 text-red-500 mx-auto mb-4" />
            <h3 className="text-xl font-bold text-gray-900 mb-2">Error Loading Payments</h3>
            <p className="text-gray-600">
              {error?.response?.data?.message || 'Failed to load payments. Please try again later.'}
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 p-3 sm:p-6">
      <div className="space-y-3 sm:space-y-4">
        {/* Header */}
        <div className="bg-white border border-gray-200 rounded-lg p-3 sm:p-4">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
            <div>
              <h1 className="text-xl sm:text-2xl font-bold text-gray-900 flex items-center gap-2">
                <CreditCard className="w-6 h-6" />
                Payments
              </h1>
              <p className="text-sm text-gray-600 mt-1">
                Manage and track all payment transactions
              </p>
            </div>
          </div>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
          <div className="bg-white border border-gray-200 rounded-lg p-3 sm:p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Total Payments</p>
                <p className="text-2xl font-bold text-gray-900 mt-1">{totalPayments}</p>
              </div>
              <CreditCard className="w-8 h-8 text-gray-400" />
            </div>
          </div>
          <div className="bg-white border border-gray-200 rounded-lg p-3 sm:p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Completed</p>
                <p className="text-2xl font-bold text-green-600 mt-1">{completedPayments}</p>
              </div>
              <CheckCircle className="w-8 h-8 text-green-500" />
            </div>
          </div>
          <div className="bg-white border border-gray-200 rounded-lg p-3 sm:p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Total Amount</p>
                <p className="text-2xl font-bold text-gray-900 mt-1">
                  ${totalAmount.toFixed(2)}
                </p>
              </div>
              <Download className="w-8 h-8 text-gray-400" />
            </div>
          </div>
        </div>

        {/* Filters */}
        <div className="bg-white border border-gray-200 rounded-lg p-3 sm:p-4">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
            <SearchSelect
              label="Status"
              value={filters.status}
              onChange={(value) => handleFilterChange('status', value)}
              options={statusOptions}
            />
            {/* <SearchSelect
              label="Payment Type"
              value={filters.payment_type}
              onChange={(value) => handleFilterChange('payment_type', value)}
              options={paymentTypeOptions}
            /> */}
            <DatePicker
              label="From Date"
              value={filters.date_from}
              onChange={(value) => handleFilterChange('date_from', value)}
            />
            <DatePicker
              label="To Date"
              value={filters.date_to}
              onChange={(value) => handleFilterChange('date_to', value)}
            />
          </div>
        </div>

        {/* Data Table */}
        <div className="bg-white border border-gray-200 rounded-lg p-3 sm:p-4">
          <DataTable
            data={payments}
            columns={columns}
            actions={actions}
            isLoading={isLoading}
            searchPlaceholder="Search payments by reference, booking, client, or vendor..."
            onSearch={handleSearch}
            emptyMessage="No payments found"
          />
        </div>
      </div>
    </div>
  );
};

export default AdminPayments;

